;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import postalCodesData from './postal-codes.json';

export interface PostalCodeRule {
  Note: string;
  Country: string;
  ISO: string;
  Format: string;
  Example: string;
  Regex: string;
}

const postalCodes: PostalCodeRule[] = postalCodesData as PostalCodeRule[];

/**
 * Get postal code validation rules for a specific country by ISO code
 * @param isoCode - The ISO country code (e.g., 'US', 'GB', 'DE')
 * @returns Array of postal code rules for the country, or null if no postal codes required
 */
export function getPostalCodeRules(isoCode: string): PostalCodeRule[] | null {
  if (!isoCode) return null;

  const rules = postalCodes.filter((rule) => rule.ISO.toUpperCase() === isoCode.toUpperCase());

  // If country has no postal code system
  if (rules.length === 0 || rules.every((rule) => rule.Format === '- no codes -' || !rule.Regex)) {
    return null;
  }

  return rules.filter((rule) => rule.Regex && rule.Format !== '- no codes -');
}

/**
 * Validate a postal code against country-specific rules
 * @param postalCode - The postal code to validate
 * @param isoCode - The ISO country code
 * @returns { isValid: boolean, format?: string } - Validation result with expected format
 */
export function validatePostalCode(
  postalCode: string,
  isoCode: string
): { isValid: boolean; format?: string; country?: string } {
  if (!postalCode || !isoCode) {
    return { isValid: false };
  }

  const rules = getPostalCodeRules(isoCode);

  // If country doesn't use postal codes, any value is invalid
  if (rules === null) {
    return { isValid: false };
  }

  // Try to match against any of the country's regex patterns
  for (const rule of rules) {
    try {
      const regex = new RegExp(rule.Regex);
      if (regex.test(postalCode.trim())) {
        return {
          isValid: true,
          format: rule.Example || rule.Format,
          country: rule.Country,
        };
      }
    } catch {
      // If regex is invalid, skip it
      console.warn(`Invalid regex for ${rule.Country}: ${rule.Regex}`);
    }
  }

  // If we have rules but none matched, return the expected format
  return {
    isValid: false,
    format: rules[0]?.Example || rules[0]?.Format,
    country: rules[0]?.Country,
  };
}

/**
 * Check if a country requires postal codes
 * @param isoCode - The ISO country code
 * @returns boolean - True if country requires postal codes
 */
export function requiresPostalCode(isoCode: string): boolean {
  const rules = getPostalCodeRules(isoCode);
  return rules !== null && rules.length > 0;
}

/**
 * Get expected postal code format(s) for display to user
 * @param isoCode - The ISO country code
 * @returns string - Formatted message about expected format(s)
 */
export function getPostalCodeFormat(isoCode: string): string | null {
  const rules = getPostalCodeRules(isoCode);

  if (!rules || rules.length === 0) {
    return null;
  }

  const examples = [...new Set(rules.map((rule) => rule.Example || rule.Format).filter(Boolean))];

  if (examples.length === 0) {
    return null;
  }

  return examples.join(' or ');
}

/**
 * Placeholder for a ZIP / postal code input, using the country's example.
 * @param isoCode - The ISO country code
 * @returns string such as "E.g. SW1A 1AA", or "Type here" when the country has no example
 */
export function getPostalCodePlaceholder(isoCode: string): string {
  const example = getPostalCodeRules(isoCode)?.find((rule) => rule.Example)?.Example;
  if (!example) return 'Type here';
  return `E.g. ${example}`;
}
